import {
    faArrowLeft,
    faBrain,
    faCalendar,
    faChartBar,
    faClipboard,
    faExclamationCircle,
    faFileText,
    faHeart,
    faPills,
    faSpinner,
    faStickyNote,
    faTimes,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Badge, Button, Col, Container, Modal, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';

import './ScanMultiModal.scss';
import { callQuery, GET_FILE_AT_VERSION, LLM_FILE_DIFF, Lab } from '../../../../amplify/api-gateway/query';
import { PatientVisit } from '../../../../amplify/api-gateway/search-patients';
import { LabsModal } from '../../Labs';
import { convertScanMultiLabsToLabArray } from '../../../../utils/lab-data-adapter';

interface ScanMultiModalVersion {
    origVersion: string;
    newVersion: string;
}

interface ScanMultiModalProps {
    isOpen: boolean;
    onClose: () => void;
    patient: PatientVisit;
    versionInfo: ScanMultiModalVersion;
    initialSection?: string,
    triggeringEvents?: string[],
}

export const ScanMultiModal = ({
    isOpen,
    onClose,
    patient,
    versionInfo,
    initialSection = undefined,
    triggeringEvents = undefined,
}: ScanMultiModalProps) => {
    const [activeSection, setActiveSection] = useState('patient');

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<any>();
    const [dataDiff, setDataDiff] = useState<any>();
    const [oldData, setOldData] = useState<any>();
    const [labsForModal, setLabsForModal] = useState<Lab[][]>([]);
    const [showLabsModal, setShowLabsModal] = useState(false);

    const currentData = data || {};

    useEffect(() => {
        setIsLoading(true);

        if (!versionInfo) {
            return;
        }

        callQuery({
            operation: LLM_FILE_DIFF,
            csn: patient.csn,
            mrn: patient.mrn,
            file_type: "scan_multi",
            old_version_id: versionInfo.origVersion, 
            new_version_id: versionInfo.newVersion,
        })
        .then((diff: any) => {
            if (diff && diff["diff"]) {
                setDataDiff(diff["diff"]);
            } else {
                setDataDiff(undefined);
            }
            if (diff && diff["newObj"]) {
                setData(diff["newObj"]);
                // Transform labs data for LabsModal with diff highlighting
                if (diff["newObj"].labs) {
                    const oldLabs = diff["oldObj"]?.labs;
                    setLabsForModal(convertScanMultiLabsToLabArray(diff["newObj"].labs, oldLabs));
                }
            } else {
                setData(undefined);
                setLabsForModal([]);
            }
            if (diff && diff["oldObj"]) {
                setOldData(diff["oldObj"]);
            } else {
                setOldData(undefined);
            }
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [versionInfo]);

    useEffect(() => {
        if (initialSection && Object.keys(currentData).includes(initialSection)) {
            setActiveSection(initialSection);
        }
    }, [initialSection, currentData]);

    const sectionIcons: any = {
        patient: faUser,
        reasons_for_visit: faFileText,
        visits: faCalendar,
        pmh: faFileText,
        home_meds: faPills,
        meds: faPills,
        orders: faFileText,
        imaging: faClipboard,
        labs: faClipboard,
        echo: faHeart,
        ecg_12L: faClipboard,
        vs_stats_by_csn: faChartBar,
        vs_1min: faChartBar,
        ecg_continuous_stats: faClipboard,
        ecg_continuous_granular: faClipboard,
        risk_scores: faExclamationCircle,
        predictions: faBrain,
        notes: faStickyNote
    };

    const sectionLabels: any = {
        patient: 'Patient Info',
        reasons_for_visit: 'Visit Reason',
        visits: 'Visits',
        pmh: 'Medical History',
        home_meds: 'Home Medications',
        meds: 'Current Medications',
        orders: 'Orders',
        imaging: 'Imaging',
        labs: 'Lab Results',
        echo: 'Echocardiogram',
        ecg_12L: '12-Lead ECG',
        vs_stats_by_csn: 'Vital Signs Stats',
        vs_1min: 'Minute Vitals',
        ecg_continuous_stats: 'Continuous ECG Stats',
        ecg_continuous_granular: 'ECG Details',
        risk_scores: 'Risk Scores',
        predictions: 'Predictions',
        notes: 'Clinical Notes'
    };

    // Helper function to deeply compare two values
    const deepEqual = (a: any, b: any): boolean => {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (typeof a !== typeof b) return false;
        
        if (typeof a === 'object') {
            if (Array.isArray(a) !== Array.isArray(b)) return false;
            
            if (Array.isArray(a)) {
                if (a.length !== b.length) return false;
                for (let i = 0; i < a.length; i++) {
                    if (!deepEqual(a[i], b[i])) return false;
                }
                return true;
            }
            
            const keysA = Object.keys(a);
            const keysB = Object.keys(b);
            if (keysA.length !== keysB.length) return false;
            
            for (let key of keysA) {
                if (!keysB.includes(key)) return false;
                if (!deepEqual(a[key], b[key])) return false;
            }
            return true;
        }
        
        return false;
    };

    // Check if a section has new data
    const hasNewData = (sectionKey: string): boolean => {
        if (!dataDiff || !oldData) return false;
        
        const newSectionData = currentData[sectionKey];
        const oldSectionData = oldData[sectionKey];
        
        // If both are empty/null/undefined, no new data
        if ((!newSectionData || (Array.isArray(newSectionData) && newSectionData.length === 0)) && 
            (!oldSectionData || (Array.isArray(oldSectionData) && oldSectionData.length === 0))) {
            return false;
        }
        
        // If new data is empty but old data existed, this is actually a removal (not "new")
        if (!newSectionData || (Array.isArray(newSectionData) && newSectionData.length === 0)) {
            return false;
        }
        
        return !deepEqual(newSectionData, oldSectionData);
    };

    // Check if a specific field/value is new
    const isNewField = (sectionKey: string, fieldKey: string, currentValue: any): boolean => {
        if (!dataDiff || !oldData) return false;
        
        const oldSectionData = oldData[sectionKey];
        const newSectionData = currentData[sectionKey];
        
        // If current value is empty/null and we're checking a field, it's likely not "new"
        if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
            return false;
        }
        
        if (!oldSectionData && newSectionData) {
            // Entire section is new, but only if it has actual content
            return !!(newSectionData && (
                !Array.isArray(newSectionData) || 
                (Array.isArray(newSectionData) && newSectionData.length > 0)
            ));
        }
        
        if (!oldSectionData) return false;
        
        // Handle arrays
        if (Array.isArray(oldSectionData) && Array.isArray(newSectionData)) {
            // Check if this is a new item in the array
            const itemIndex = parseInt(fieldKey);
            if (!isNaN(itemIndex)) {
                return itemIndex >= oldSectionData.length;
            }
            return newSectionData.length > oldSectionData.length;
        }
        
        // Handle objects
        if (typeof oldSectionData === 'object' && oldSectionData !== null) {
            const oldValue = oldSectionData[fieldKey];
            return !deepEqual(oldValue, currentValue);
        }
        
        return !deepEqual(oldSectionData, newSectionData);
    };

    // Check if an entire array item is new
    const isNewArrayItem = (sectionKey: string, itemIndex: number): boolean => {
        if (!oldData) return false;
        
        const oldSectionData = oldData[sectionKey];
        const newSectionData = currentData[sectionKey];
        
        if (!Array.isArray(oldSectionData) || !Array.isArray(newSectionData)) return false;
        
        // Only consider it new if the new array actually has content at this index
        if (!newSectionData[itemIndex]) return false;
        
        return itemIndex >= oldSectionData.length;
    };

    const renderValue = (value: any, isNew = false): JSX.Element => {
        const style = isNew
            ? { backgroundColor: '#fef3c7', border: '1px solid #f59e0b', padding: '4px' }
            : {};

        if (value === null || value === undefined) {
            return <span style={{ ...style, fontStyle: 'italic', color: '#6b7280' }}>Not available</span>;
        }

        if (typeof value === 'boolean') {
            return <span style={style}>{value ? 'Yes' : 'No'}</span>;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return <span style={style}>{value.toString()}</span>;
        }

        if (Array.isArray(value)) {
            return (
                <ul style={{ ...style, listStyleType: 'disc', marginLeft: '1rem' }}>
                    {value.map((item, index) => (
                        <li key={index}>{renderValue(item)}</li>
                    ))}
                </ul>
            );
        }

        if (typeof value === 'object') {
            return (
                <dl style={{ ...style, marginLeft: '1rem' }}>
                    {Object.entries(value).map(([key, val], idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                            <dt style={{ fontWeight: 'bold' }}>{key}:</dt>
                            <dd style={{ marginLeft: '1rem' }}>
                                {(key === "note_text" && typeof val === 'string') ? 
                                    <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '600px', overflowY: 'auto' }}>{val}</pre> : 
                                    renderValue(val)
                                }
                            </dd>
                        </div>
                    ))}
                </dl>
            );
        }

        // Fallback
        return <span style={style}>{String(value)}</span>;
    };

    const renderTable = (data: any, sectionKey: string) => {
        if (!data) return <div className="text-muted fst-italic">No data available</div>;

        // Special handling for labs section - use unified Labs.tsx modal
        if (sectionKey === 'labs') {
            return (
                <div className="text-center py-4">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => setShowLabsModal(true)}
                    >
                        <FontAwesomeIcon icon={faClipboard} className="me-2" />
                        View Labs in Detailed Modal
                    </Button>
                    <div className="text-muted mt-2">
                        <small>Lab results will open in an enhanced view with time navigation and abnormal value highlighting</small>
                    </div>
                </div>
            );
        }

        if (Array.isArray(data)) {
            if (data.length === 0) return <div className="text-muted fst-italic">No records found</div>;

            return (
                <div style={{ overflowY: 'auto' }}>
                    {data.map((item, index) => {
                        const isNewItem = isNewArrayItem(sectionKey, index);
                        
                        return (
                            <div key={index} className="card mb-3" style={isNewItem ? { backgroundColor: '#fef3c7' } : {}}>
                                <div className="card-header bg-light">
                                    <h6 className="card-title mb-0">
                                        Record {index + 1}
                                        {isNewItem && (
                                            <Badge bg="warning" text="dark" className="ms-2">
                                                New
                                            </Badge>
                                        )}
                                    </h6>
                                </div>
                                <div className="card-body p-0">
                                    <Table striped bordered hover responsive size='sm' className="mb-0">
                                        <tbody>
                                            {Object.entries(item).map(([key, value]) => {
                                                const fieldIsNew = isNewField(sectionKey, key, value) || isNewItem;
                                                
                                                return (
                                                    <tr key={key} style={fieldIsNew ? { backgroundColor: '#fef3c7' } : {}}>
                                                        <td className="bg-light fw-bold" style={{ width: '30%' }}>
                                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            {fieldIsNew && !isNewItem && (
                                                                <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.6em' }}>
                                                                    Updated
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td className="font-monospace">
                                                            {renderValue(value, fieldIsNew)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (typeof data === 'object') {
            return (
                <div className="card">
                    <div className="card-body p-0">
                        <Table striped bordered hover responsive className="mb-0">
                            <tbody>
                                {Object.entries(data).map(([key, value]) => {
                                    const fieldIsNew = isNewField(sectionKey, key, value);
                                    
                                    return (
                                        <tr key={key} style={fieldIsNew ? { backgroundColor: '#fef3c7' } : {}}>
                                            <td className="bg-light fw-bold" style={{ width: '30%' }}>
                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                {fieldIsNew && (
                                                    <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.6em' }}>
                                                        Updated
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                {typeof value === 'object' && value !== null ? (
                                                    <div>
                                                        {Object.entries(value).map(([subKey, subValue]) => {
                                                            const subFieldIsNew = isNewField(sectionKey, subKey, subValue);
                                                            
                                                            return (
                                                                <div key={subKey} className="mb-1" style={subFieldIsNew ? { backgroundColor: '#fef3c7', padding: '2px' } : {}}>
                                                                    <strong className="text-muted">{subKey}:</strong>{' '}
                                                                    {renderValue(subValue, subFieldIsNew)}
                                                                    {subFieldIsNew && (
                                                                        <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.6em' }}>
                                                                            Updated
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    renderValue(value, fieldIsNew)
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-body">
                    {renderValue(data, hasNewData(sectionKey))}
                </div>
            </div>
        );
    };

    const renderTriggeringEvent = (event: any) => {
        if (event === "TRIAGE") {
            return (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip>
                            Patient was triaged
                        </Tooltip>
                    }
                >
                    <span className="notes-highlight notes-highlight-orange">TRIAGE</span>
                </OverlayTrigger>
            )
        } else if (event === "DEPARTURE") {
            return (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip>
                            Patient departed the ED
                        </Tooltip>
                    }
                >
                    <span className="notes-highlight notes-highlight-orange">DEPARTURE</span>
                </OverlayTrigger>
            )
        } else if (event === "ARRIVAL") {
            return (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip>
                            Patient arrived in the ED
                        </Tooltip>
                    }
                >
                    <span className="notes-highlight notes-highlight-orange">ARRIVAL</span>
                </OverlayTrigger>
            )
        } else {
            return (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip>
                            Patient had new data in {event}
                        </Tooltip>
                    }
                >
                    <span className="notes-highlight notes-highlight-orange">{event}</span>
                </OverlayTrigger>
            )
        }
    };

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            size="xl"
            fullscreen
            backdrop="static"
            className="scan-multi-modal"
        >
            <Modal.Body className="p-0" style={{ height: '100vh' }}>
                {
                    isLoading ? (
                        <Container fluid style={{paddingTop: '100px', textAlign: 'center'}}>
                            <Row>
                                <Col md={12}>
                                    <h1 style={{color: '#AAA', fontSize: '16px'}}><FontAwesomeIcon spin icon={faSpinner} /> Loading...</h1>
                                </Col>
                            </Row>
                        </Container>
                    ) : (
                        <Container fluid className="h-100">
                            <Row className="h-100">
                                {/* Left Sidebar */}
                                <Col xs={12} md={3} className="border-end h-100 d-flex flex-column">
                                    <div className="py-2 border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex">
                                                <Button
                                                    variant="primary"
                                                    className={`dashboard-round-btn`}
                                                    onClick={onClose}
                                                >
                                                    <FontAwesomeIcon icon={faArrowLeft} />
                                                </Button>
                                                <h5 className="mt-1">Supporting Data</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="nav-list">
                                        {
                                            triggeringEvents && (
                                                <div className="nav-info mt-2 mb-2">
                                                    Last update triggered by new data in {triggeringEvents.map((event: any) => {
                                                        return renderTriggeringEvent(event);
                                                    })}
                                                </div>
                                            )
                                        }
                                        {/* <div className="nav-info mt-2 mb-2">
                                            {
                                                JSON.stringify(versionInfo)
                                            }
                                        </div> */}
                                        {Object.keys(currentData).map((sectionKey) => {
                                            const iconName = sectionIcons[sectionKey] || faFileText;
                                            const hasNew = hasNewData(sectionKey);

                                            return (
                                                <div key={sectionKey}>
                                                    <div className={`nav-item ${activeSection === sectionKey && "nav-item-active"}`}
                                                        onClick={() => setActiveSection(sectionKey)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <FontAwesomeIcon icon={iconName} className="me-2" />
                                                        <span className="fw-medium me-2">
                                                            {sectionLabels[sectionKey] || sectionKey}
                                                        </span>
                                                        {hasNew && (
                                                            <OverlayTrigger
                                                                placement="bottom"
                                                                overlay={
                                                                    <Tooltip>
                                                                        New data since the last summary update
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Badge bg="warning" text="dark">
                                                                    Updated
                                                                </Badge>
                                                            </OverlayTrigger>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Col>

                                {/* Main Content */}
                                <Col xs={12} md={9} className="h-100 d-flex flex-column">
                                    <div className="p-2 border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon
                                                    icon={sectionIcons[activeSection] || faFileText}
                                                    className="me-2 text-secondary"
                                                    size="lg"
                                                />
                                                <h6 className="mb-0">
                                                    {sectionLabels[activeSection] || activeSection}
                                                </h6>
                                                {hasNewData(activeSection) && (
                                                    <Badge bg="warning" text="dark" className="ms-3">
                                                        Updated
                                                    </Badge>
                                                )}
                                            </div>
                                            <Button
                                                onClick={onClose}
                                                aria-label="Close"
                                                variant='link-secondary'
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="flex-grow-1 p-3" style={{ overflowY: 'auto' }}>
                                        {renderTable(currentData[activeSection], activeSection)}
                                    </div>
                                </Col>
                            </Row>
                            {/* Debug section - remove in production
                            // <Row style={{ display: 'none' }}>
                            //     {JSON.stringify(data)}
                            //     <hr />
                            //     {JSON.stringify(dataDiff)}
                            //     <hr />
                            //     {JSON.stringify(oldData)}
                            // </Row> */}
                        </Container>
                    )
                }
            </Modal.Body>

            {/* Labs Modal - using unified Labs.tsx component */}
            <LabsModal
                showModal={showLabsModal}
                setShowModal={setShowLabsModal}
                labs={labsForModal}
                patientVisit={patient}
                dataSource="snapshot"
                fullscreen={true}
            />
        </Modal>
    );
};
